/**
 * Which clock a passenger reads, on the public booking site.
 *
 * Every time on this site used to render in the TENANT's timezone. For a
 * cross-border route that is wrong for one end of almost every journey: a rider
 * boarding in San Miguel de Allende was shown a Texas clock, and — worse — an
 * arrival into the other country was printed on the origin side's clock, a
 * wall-clock time no clock at that bus station will ever show.
 *
 * The rule here is the same one the printed ticket and the server reports
 * already follow (PR B2 / D-B1): **a time renders in the timezone of the city
 * the passenger is standing in when it happens.** Departure belongs to the
 * origin city, arrival to the destination city, each intermediate stop to
 * itself.
 *
 * `cities.timezone` defaults to `'UTC'` and means "not backfilled yet", so the
 * shared resolver falls back to the tenant's zone until the backfill runs —
 * which keeps today's behaviour rather than suddenly rendering UTC.
 */
import type { Locale } from "date-fns";
import {
  formatInTimezone,
  getTimezoneAbbreviation,
} from "@shared/timezone-utils";
import { resolveStopTimezone } from "@shared/utils/timezoneResolve";

/** The minimum a city payload must carry to be rendered in its own zone. */
export interface CityWithTimezone {
  name?: string | null;
  timezone?: string | null;
}

/**
 * Resolve the zone for one endpoint of a journey.
 *
 * `originTimezone` is passed as the second candidate so an un-backfilled
 * intermediate stop inherits the route's own zone rather than the tenant's —
 * the same order `resolveStopTimezone` uses server-side.
 */
export function zoneForCity(
  city: CityWithTimezone | null | undefined,
  originTimezone: string | null | undefined,
  tenantTimezone: string | null | undefined,
): string {
  return resolveStopTimezone(city?.timezone, originTimezone, tenantTimezone);
}

/**
 * Do these two zones differ? Compared as IANA identifiers, never as
 * abbreviations: `America/Mexico_City` and `America/Chicago` are BOTH "CST" in
 * winter, so comparing abbreviations would hide exactly the border case that
 * makes labelling necessary.
 */
export function zonesDiffer(a: string, b: string): boolean {
  return a !== b;
}

export interface RenderedTime {
  /** e.g. "13:00" */
  time: string;
  /** e.g. "PM" */
  period: string;
  /** e.g. "Mon, 3 Nov" */
  date: string;
  /** Calendar date key in this zone, for comparing days across zones. */
  dateKey: string;
  /** "CST"/"CDT" — caller decides whether to show it. */
  abbr: string;
  timezone: string;
}

/** Formats one instant in one zone, with everything a card needs. */
export function renderTime(
  instant: Date | string,
  timezone: string,
  /** date-fns locale for the weekday/month names (page language). */
  locale?: Locale,
): RenderedTime {
  const value = typeof instant === "string" ? new Date(instant) : instant;
  return {
    time: formatInTimezone(value, timezone, "HH:mm"),
    period: formatInTimezone(value, timezone, "a"),
    date: formatInTimezone(value, timezone, "EEE, d MMM", locale),
    dateKey: formatInTimezone(value, timezone, "yyyy-MM-dd"),
    // Computed for THIS instant, not for "now" — otherwise a January departure
    // is labelled CDT on a page loaded in July.
    abbr: getTimezoneAbbreviation(timezone, value),
    timezone,
  };
}

/**
 * Does the journey arrive on a later calendar day than it departed?
 *
 * Each side is judged in its OWN zone, because "arrives next day" is a claim
 * about the passenger's experience: they board on one local date and step off
 * on another. Comparing both ends in a single zone — which is what this site
 * did — can both invent and hide a day change across a border.
 */
export function dayOffsetBetween(
  departure: RenderedTime,
  arrival: RenderedTime,
): number {
  const [dy, dm, dd] = departure.dateKey.split("-").map(Number);
  const [ay, am, ad] = arrival.dateKey.split("-").map(Number);
  const depUtc = Date.UTC(dy, dm - 1, dd);
  const arrUtc = Date.UTC(ay, am - 1, ad);
  return Math.round((arrUtc - depUtc) / 86_400_000);
}
